/**
 * Full-file streaming row parse for the scrub pass, over both CSV and XLSX behind
 * one async-iterator surface so the orchestrator is parser-agnostic.
 *
 *   - **CSV (Papa Parse, streaming):** Papa reads the `File` via sliced
 *     `FileReader` chunks internally — constant memory, never the whole file. We
 *     consume its `chunk` callback (cheaper than per-row `step` at ~1M rows) and
 *     bridge it into an async iterator with explicit backpressure: the parser is
 *     PAUSED while the consumer (HMAC + emit + upload) drains a chunk and RESUMED
 *     when it asks for more. Working set ≈ one chunk, independent of file size.
 *   - **XLSX (SheetJS):** SheetJS has no true streaming reader in the browser — it
 *     materializes the whole workbook. The `XLSX_SIZE_GUARD_BYTES` guard (applied
 *     at preflight in `read-headers.ts`) keeps large portfolios on the CSV path;
 *     here we read the workbook once and yield its rows. KNOWN, DOCUMENTED
 *     LIMITATION: xlsx is memory-bound; CSV is the path for 500k–1M-row files.
 *
 * `xlsx` is imported dynamically (lazy uploader chunk). Browser-only.
 */

import Papa from 'papaparse';
import { XLSX_SIZE_GUARD_BYTES } from '../../common/uploader';
import type { ColumnHeader, UploadFormat } from '../../common/uploader';
import { XlsxTooLargeError } from './errors';
import { coerceXlsxCellValue } from './xlsx-cell';

/** One parsed data row, cells aligned to `headers` by index (header consumed). */
export interface ParsedRow {
  readonly cells: readonly string[];
}

/**
 * File-level parse-structure signals accumulated during the streaming pass
 * (P5 / F6). The scrub pass is the ONLY place that sees raw parse structure, so
 * these counts are captured here and shipped at transfer-complete — the
 * accept-and-warn posture: rows are still accepted (extra cells sliced to the
 * header width, short rows kept), but the divergence is COUNTED, never silent.
 */
export interface ParseDataQuality {
  /** Data rows whose parsed cell count differed from the header width. */
  readonly raggedRowCount: number;
  /** Parser-reported structural errors (Papa per-chunk `results.errors`). */
  readonly parseErrorCount: number;
}

/**
 * A streaming view over a file's rows. `headers` is available before iteration
 * (read from the first row); `rows()` yields data rows; `rowsSeen()` reports
 * progress.
 */
export interface RowStream {
  readonly headers: readonly ColumnHeader[];
  /** Async iterator of data rows. Iterate exactly once. */
  rows(): AsyncIterable<ParsedRow>;
  /** Count of data rows yielded so far — for the progress UI. */
  rowsSeen(): number;
  /** Accumulated parse-structure signals so far (final once iteration completes). */
  dataQuality(): ParseDataQuality;
}

/** Trim a header cell and wrap it as a {@link ColumnHeader} at its position. */
function toHeaders(rawHeader: readonly string[]): ColumnHeader[] {
  return rawHeader.map((name, index) => ({ index, name: name.trim() }));
}

/**
 * Open a streaming row view over `file`. The header row is read first so
 * `headers` is populated before `rows()` is iterated.
 *
 * @throws {XlsxTooLargeError} when an `.xlsx` exceeds `XLSX_SIZE_GUARD_BYTES`.
 */
export async function openRowStream(file: File, format: UploadFormat): Promise<RowStream> {
  return format === 'xlsx' ? openXlsxRowStream(file) : openCsvRowStream(file);
}

// ── CSV: Papa Parse chunk callback bridged to an async iterator ───────────────

/**
 * Bridges Papa's push-based `chunk` callback to a pull-based async iterator with
 * single-chunk backpressure. The parser is paused as soon as a chunk with data
 * arrives and resumed only when the consumer has drained that chunk, so at most
 * one chunk's rows are in memory at a time.
 *
 * Coordination is via a single `signal` promise the consumer awaits whenever it
 * has no buffered rows and the parse isn't finished. The crucial ordering: the
 * consumer arms a fresh `signal` (so a wake can't be lost) BEFORE it calls
 * `resume()` — `resume()` can re-enter the `chunk`/`complete` callbacks
 * synchronously, and the armed signal guarantees that wake is delivered.
 */
function openCsvRowStream(file: File): Promise<RowStream> {
  return new Promise<RowStream>((resolveStream, rejectStream) => {
    let headers: ColumnHeader[] | null = null;
    let seen = 0;
    // P5/F6 data-quality accumulators: parser-reported structural errors (per
    // chunk) + rows whose width diverges from the header (counted at yield).
    let raggedRowCount = 0;
    let parseErrorCount = 0;

    // The single in-flight chunk's data rows and a cursor into them.
    let buffer: string[][] = [];
    let bufferIndex = 0;
    let finished = false;
    let failure: Error | null = null;
    let parserRef: Papa.Parser | null = null;

    // A one-shot wake channel: `signal` resolves when the producer has advanced
    // the state (new buffer, finished, or failure). `wake` resolves the current
    // signal; `arm` installs a fresh one. The consumer always arms before it
    // resumes the parser so a synchronous callback re-entry can't drop the wake.
    let resolveSignal: (() => void) | null = null;
    let signal: Promise<void> = new Promise<void>((r) => {
      resolveSignal = r;
    });
    const wake = (): void => {
      const r = resolveSignal;
      if (r) {
        resolveSignal = null;
        r();
      }
    };
    const arm = (): void => {
      if (resolveSignal === null) {
        signal = new Promise<void>((r) => {
          resolveSignal = r;
        });
      }
    };

    Papa.parse<string[]>(file, {
      skipEmptyLines: 'greedy',
      chunk: (results, parser) => {
        parserRef = parser;
        if (failure) {
          parser.abort();
          return;
        }
        // F6: Papa's per-chunk structural errors (malformed quoting etc.) used to
        // be DISCARDED (only the fatal callback fired) — an unquoted comma could
        // shift every later cell entirely silently. Rows are still accepted
        // (accept-and-warn); the count is shipped as a file-level signal.
        parseErrorCount += results.errors.length;
        const data = results.data;
        let startRow = 0;
        if (headers === null) {
          // First row of the first chunk is the header.
          headers = toHeaders(data[0] ?? []);
          startRow = 1;
          resolveStream(stream);
        }
        buffer = data.slice(startRow);
        bufferIndex = 0;
        // Pause until the consumer drains this chunk (only if it carries rows).
        if (buffer.length > 0) {
          parser.pause();
        }
        wake();
      },
      complete: () => {
        finished = true;
        // A header-only / empty file never entered the chunk branch that resolves
        // the stream; resolve it here so the caller isn't left hanging.
        if (headers === null) {
          headers = [];
          resolveStream(stream);
        }
        wake();
      },
      error: (error: Error) => {
        failure = error;
        finished = true;
        if (headers === null) {
          rejectStream(error);
        }
        wake();
      },
    });

    const width = (): number => headers?.length ?? 0;

    async function* rowGenerator(): AsyncGenerator<ParsedRow> {
      // `finally` runs whenever the consumer stops pulling — normal completion,
      // a thrown failure, OR the consumer abandoning the iterator (AbortError,
      // HMAC failure, transport error, `break`). `parser.abort()` tears down Papa
      // so it stops reading the File and releases the chunk it holds; without this,
      // a consumer-side stop leaks the parser (and its File/chunk) until GC.
      try {
        for (;;) {
          if (failure) throw failure;
          if (bufferIndex < buffer.length) {
            const raw = buffer[bufferIndex] ?? [];
            bufferIndex += 1;
            seen += 1;
            const w = width();
            // F6: a width divergence (an unquoted comma pushing cells over, or a
            // short row) is COUNTED, then the row is still accepted with extra
            // cells sliced to the header width (accept-and-warn — slice stays).
            if (w > 0 && raw.length !== w) raggedRowCount += 1;
            yield { cells: w > 0 ? raw.slice(0, w) : raw };
            continue;
          }
          if (finished) {
            if (failure) throw failure;
            return;
          }
          // Buffer drained and more is coming. Arm the wake channel, resume the
          // parser (may synchronously deliver the next chunk + wake), then await.
          arm();
          if (parserRef) parserRef.resume();
          await signal;
        }
      } finally {
        parserRef?.abort();
      }
    }

    const stream: RowStream = {
      get headers() {
        return headers ?? [];
      },
      rows: () => rowGenerator(),
      rowsSeen: () => seen,
      dataQuality: () => ({ raggedRowCount, parseErrorCount }),
    };
  });
}

// ── XLSX: materialized read (size-guarded), yielded as rows ───────────────────

async function openXlsxRowStream(file: File): Promise<RowStream> {
  if (file.size > XLSX_SIZE_GUARD_BYTES) {
    throw new XlsxTooLargeError(file.size, XLSX_SIZE_GUARD_BYTES);
  }
  const XLSX = await import('xlsx');
  const buffer = await file.arrayBuffer();
  // F4/F37: `cellDates` + `raw: true` read each cell's UNDERLYING STORED VALUE
  // (full-precision numbers, typed dates), never the formatted display text —
  // display rounding (`0.0%`, `#,##0`, `m/d/yy`) must not become the persisted
  // fact. Every cell then flows through the ONE `coerceXlsxCellValue` coercion,
  // identical to the preflight path in read-headers.ts (byte-identical rule).
  const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  const firstSheetName = workbook.SheetNames[0];
  const sheet = firstSheetName !== undefined ? workbook.Sheets[firstSheetName] : undefined;
  const matrix =
    sheet !== undefined
      ? XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: true, defval: '', blankrows: false })
      : [];

  const headers = toHeaders((matrix[0] ?? []).map((cell) => coerceXlsxCellValue(cell)));
  const width = headers.length;
  const dataRows = matrix.slice(1);
  let seen = 0;
  // F6: same width-divergence signal as the CSV path (a data row extending past
  // the header row's width — sliced but counted).
  let raggedRowCount = 0;

  // A sync generator adapted to an async iterable: the xlsx workbook is already
  // fully materialized (size-guarded), so there is no async work per row.
  function* syncRows(): Generator<ParsedRow> {
    for (const row of dataRows) {
      if (width > 0 && row.length !== width) raggedRowCount += 1;
      const cells = row
        .map((cell) => coerceXlsxCellValue(cell))
        .slice(0, width > 0 ? width : undefined);
      seen += 1;
      yield { cells };
    }
  }
  const rowGenerator = (): AsyncIterable<ParsedRow> => {
    const iterator = syncRows();
    return {
      [Symbol.asyncIterator]: () => ({
        next: () => Promise.resolve(iterator.next()),
      }),
    };
  };

  return {
    headers,
    rows: () => rowGenerator(),
    rowsSeen: () => seen,
    // SheetJS surfaces no per-row structural errors (the workbook either reads or
    // throws), so parseErrorCount is structurally 0 on the XLSX path.
    dataQuality: () => ({ raggedRowCount, parseErrorCount: 0 }),
  };
}
